// Leads.jsx - People who started the kit form and never finished it.
//
// Deliberately its own screen and not a filter on Orders. These people have not
// agreed to anything, are not owed anything, and nothing here is a queue somebody
// has to clear. It is a list to work through when there is time, which is exactly
// why it sits below Orders in the sidebar rather than above it.
//
// There is no button to text anybody. A tick on screen two of a form that was
// never submitted is not prior express written consent, so the only outbound
// action is one email, sent by hand, once. Phone numbers are click to call.
//
// LEGEND
//   1. STATUS_TONE / FILTERS   the vocabulary the list filters on
//   2. Row                      one unfinished form, expandable
//   3. Leads                    the page

import { useState } from "react";
import clsx from "clsx";

import { useAuth } from "./AdminApp.jsx";
import { useAsync, useDebounced } from "../lib/hooks.js";
import { adminApi } from "../lib/api.js";
import { ago, dayTime, phone as formatPhone, titleCase, METAL_LABELS } from "../lib/format.js";
import { Panel, Input, Loading, Alert, Empty, Tag, Button, Modal, Textarea, useToast } from "../ui/kit.jsx";
import { Loupe, Note, Envelope, Phone, Trash, Check, Close, Refresh, ChevronDown } from "../ui/icons.jsx";

////////// 1. VOCABULARY //////////

const STATUS_TONE = {
  open: "caution",
  contacted: "bronze",
  converted: "positive",
  dismissed: "neutral",
};

const FILTERS = [
  { key: "", label: "Worth a look", status: "open,contacted" },
  { key: "open", label: "Not contacted", status: "open" },
  { key: "contacted", label: "Followed up", status: "contacted" },
  { key: "converted", label: "Came back", status: "converted" },
  { key: "dismissed", label: "Dismissed", status: "dismissed" },
];

////////// 2. ROW //////////

function Row({ lead, onChanged }) {
  const toast = useToast();
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(lead.note || "");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const act = async (work, message) => {
    setBusy(true);
    try {
      await work();
      if (message) toast.success(message);
      onChanged();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  const name = [lead.customer?.firstName, lead.customer?.lastName].filter(Boolean).join(" ");
  const where = [lead.customer?.address?.city, lead.customer?.address?.state].filter(Boolean).join(", ");

  return (
    <li className="border-b border-rule-soft last:border-0">
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="flex items-center gap-2 min-w-0 flex-1 text-left"
          aria-expanded={open}
        >
          <ChevronDown size={15} className={clsx("shrink-0 text-faint transition-transform", open && "rotate-180")} />
          <span className="min-w-0">
            <span className="block text-[14px] font-medium text-ink truncate">
              {name || "No name given"}
            </span>
            <span className="block text-[12.5px] text-faint num truncate">
              {lead.customer?.email || "no email"}
              {lead.customer?.phone ? ` · ${formatPhone(lead.customer.phone)}` : ""}
              {where ? ` · ${where}` : ""}
            </span>
          </span>
        </button>

        <span className="text-[12.5px] text-muted hidden md:block max-w-[13rem] truncate">
          {lead.reachedLabel}
        </span>

        <Tag tone={STATUS_TONE[lead.status] || "neutral"}>
          {lead.status === "converted" ? `Came back${lead.convertedNumber ? ` · ${lead.convertedNumber}` : ""}` : lead.status}
        </Tag>

        <span className="text-[12px] text-faint whitespace-nowrap w-20 text-right">{ago(lead.lastSeenAt)}</span>
      </div>

      {open && (
        <div className="px-4 pb-4 pt-1 fade">
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:gap-6">
            <div>
              <p className="eyebrow mb-2">What they told us</p>
              <dl className="text-[13px] divide-y divide-rule-soft">
                <div className="flex justify-between gap-4 py-1.5">
                  <dt className="text-muted">Metals tapped</dt>
                  <dd className="text-ink text-right">
                    {lead.chips?.length
                      ? lead.chips.map((chip) => METAL_LABELS[chip] || titleCase(chip)).join(", ")
                      : "None"}
                  </dd>
                </div>
                <div className="flex justify-between gap-4 py-1.5">
                  <dt className="text-muted">Items described</dt>
                  <dd className="text-ink text-right">{lead.declared?.length || 0}</dd>
                </div>
                <div className="flex justify-between gap-4 py-1.5">
                  <dt className="text-muted">Got as far as</dt>
                  <dd className="text-ink text-right">{lead.reachedLabel}</dd>
                </div>
                <div className="flex justify-between gap-4 py-1.5">
                  <dt className="text-muted">First seen</dt>
                  <dd className="text-ink text-right">{dayTime(lead.firstSeenAt)}</dd>
                </div>
                {lead.context?.utmCampaign && (
                  <div className="flex justify-between gap-4 py-1.5">
                    <dt className="text-muted">Campaign</dt>
                    <dd className="text-ink text-right num">
                      {[lead.context.utmSource, lead.context.utmCampaign].filter(Boolean).join(" / ")}
                    </dd>
                  </div>
                )}
                {lead.contactedAt && (
                  <div className="flex justify-between gap-4 py-1.5">
                    <dt className="text-muted">Followed up</dt>
                    <dd className="text-ink text-right">
                      {dayTime(lead.contactedAt)}
                      <span className="text-faint"> · {lead.contactedVia === "auto" ? "automatic" : "by hand"}</span>
                    </dd>
                  </div>
                )}
              </dl>

              {lead.declared?.length > 0 && (
                <ul className="mt-3 space-y-1">
                  {lead.declared.map((item, index) => (
                    <li key={index} className="text-[13px] text-muted">
                      {item.description || METAL_LABELS[item.metal] || "Item"}
                      {item.approxWeight ? ` · about ${item.approxWeight}${item.unit || "g"}` : ""}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div>
              <p className="eyebrow mb-2">What you can do</p>

              <div className="flex flex-wrap gap-2">
                {lead.customer?.phone && (
                  <a href={`tel:${lead.customer.phone}`} className="btn btn-quiet btn-sm">
                    <Phone size={14} /> Call
                  </a>
                )}
                {lead.customer?.email && lead.status !== "converted" && (
                  <Button
                    size="sm"
                    variant="quiet"
                    loading={busy}
                    disabled={Boolean(lead.contactedAt)}
                    onClick={() => act(() => adminApi.emailLead(lead._id), "Reminder sent")}
                  >
                    <Envelope size={14} />
                    {lead.contactedAt
                      ? (lead.contactedVia === "auto" ? "Reminder sent automatically" : "Already emailed")
                      : "Send the reminder now"}
                  </Button>
                )}
                {lead.status !== "dismissed" && lead.status !== "converted" && (
                  <Button
                    size="sm"
                    variant="ghost"
                    loading={busy}
                    onClick={() => act(() => adminApi.updateLead(lead._id, { status: "dismissed" }), "Dismissed")}
                  >
                    <Close size={14} /> Dismiss
                  </Button>
                )}
                {lead.status === "dismissed" && (
                  <Button
                    size="sm"
                    variant="ghost"
                    loading={busy}
                    onClick={() => act(() => adminApi.updateLead(lead._id, { status: "open" }), "Back on the list")}
                  >
                    <Refresh size={14} /> Undismiss
                  </Button>
                )}
                {can("orders.delete") && (
                  <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(true)} aria-label="Delete">
                    <Trash size={14} />
                  </Button>
                )}
              </div>

              <p className="hint mt-2.5 leading-relaxed">
                No texting from here. They ticked a box on a form they never sent, which is not
                consent to a message. A phone call is fine, and one reminder email goes out on its
                own a couple of hours after somebody leaves.
              </p>

              <Textarea
                label="Note"
                rows={2}
                wrapClassName="mt-3"
                placeholder="What happened when you called."
                value={note}
                onChange={(event) => setNote(event.target.value)}
                onBlur={() => note !== (lead.note || "") && act(() => adminApi.updateLead(lead._id, { note }))}
              />
            </div>
          </div>
        </div>
      )}

      <Modal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Delete this record?"
        description="It disappears now rather than when it expires on its own."
        footer={
          <>
            <Button variant="quiet" onClick={() => setConfirmDelete(false)}>Cancel</Button>
            <Button variant="danger" onClick={() => act(() => adminApi.deleteLead(lead._id), "Deleted")}>
              Delete
            </Button>
          </>
        }
      >
        <Alert tone="warning">
          Dismissing is usually better. It takes them off the list without destroying the record of
          somebody who asked us to delete their details.
        </Alert>
      </Modal>
    </li>
  );
}

////////// 3. PAGE //////////

export default function Leads() {
  const [filter, setFilter] = useState(FILTERS[0]);
  const [search, setSearch] = useState("");
  const debounced = useDebounced(search, 300);

  const { data, loading, error, reload } = useAsync(
    (signal) => adminApi.leads({ status: filter.status, q: debounced || undefined, limit: 100 }, signal),
    [filter.status, debounced]
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1200px]">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <h1 className="display display-lg text-ink">Bounced forms</h1>
          <p className="hint mt-1 measure leading-relaxed">
            People who started a kit request and left before sending it. Separate from orders,
            because none of this is an order yet.
          </p>
        </div>
        <Button size="sm" variant="quiet" onClick={reload}><Refresh size={15} /> Refresh</Button>
      </header>

      <div className="flex flex-col lg:flex-row gap-3 mb-5">
        <div className="lg:w-72 shrink-0">
          <Input
            placeholder="Name, email or phone"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            prefix={<Loupe size={16} />}
            className="pl-9"
            aria-label="Search unfinished forms"
          />
        </div>
        <div className="chip-row">
          {FILTERS.map((entry) => (
            <button
              key={entry.label}
              type="button"
              onClick={() => setFilter(entry)}
              className={clsx("btn btn-sm", filter.label === entry.label ? "btn-ink" : "btn-quiet")}
            >
              {entry.label}
              {data?.counts?.[entry.key] > 0 && entry.key && (
                <span className="ml-1.5 text-[11px] opacity-70">{data.counts[entry.key]}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {error && <Alert tone="error" className="mb-4">{error.message}</Alert>}

      <Panel className="overflow-hidden">
        {loading ? (
          <Loading />
        ) : !data?.items?.length ? (
          <Empty title={debounced ? "Nothing matched that" : "Nobody has bounced"} icon={Note}>
            {debounced
              ? `Nothing matches "${debounced}".`
              : "Unfinished forms appear here once somebody has given us a way to reach them."}
          </Empty>
        ) : (
          <ul>
            {data.items.map((lead) => <Row key={lead._id} lead={lead} onChanged={reload} />)}
          </ul>
        )}
      </Panel>

      <p className="hint mt-4 leading-relaxed measure-wide">
        <Check size={14} className="inline -mt-0.5 mr-1 text-bronze" />
        Records are only kept once somebody has given an email or a phone number, they are deleted
        automatically after the retention period set in Settings, and anyone who comes back and
        finishes moves to Orders on their own. One reminder email is sent automatically, once, with a
        working unsubscribe; anybody who uses it is never emailed again.
      </p>
    </div>
  );
}
